import React, { useEffect, useMemo, useState } from "react";
import Select, { SingleValue } from "react-select";
import { Bookmark, CornerDownRight, FileText, Loader2, Search, SlidersHorizontal, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import {
    Conversation,
    getMyConversations,
    Message,
    searchMyChatMessages,
    toggleChatBookmark,
} from "../../../services/chatService/ChatService";
import MilestoneAvatar from "../../ui/Avatar/MilestoneAvatar";
import { isAutomatedBotConversation } from "../../../utilities/chat/conversationUtils";
import "../formFillComponents/DynamicFormComponents/FormInput.css";

type ConversationOption = { value: number; label: string };

const authorName = (message: Message) => message.author?.displayName
    || [message.author?.firstName, message.author?.lastName].filter(Boolean).join(" ")
    || message.author?.email
    || "Unknown sender";

const messageText = (message: Message) => (message.messagePlaintext || message.messageContent || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim() || (message.attachmentUrl ? "Shared a file" : "Message");

const conversationName = (conversation: Conversation | undefined, currentUserId?: string) => {
    if (!conversation) return "Conversation";
    if (conversation.channel?.name) return conversation.channel.name;
    if (isAutomatedBotConversation(conversation, currentUserId)) return "Waivera";
    const names = conversation.participants
        .filter(participant => participant.id !== currentUserId)
        .map(participant => participant.displayName || [participant.firstName, participant.lastName].filter(Boolean).join(" ") || participant.email)
        .filter(Boolean);
    return names.join(", ") || "Conversation";
};

const ChatGlobalSearchView: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [conversationId, setConversationId] = useState(0);
    const [filesOnly, setFilesOnly] = useState(false);
    const [includeThreads, setIncludeThreads] = useState(true);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [results, setResults] = useState<Message[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const pageSize = 30;
    const canSearch = query.trim().length >= 2 || filesOnly;

    useEffect(() => {
        let cancelled = false;
        const loadConversations = async () => {
            if (!currentUser) return;
            try {
                const data = await getMyConversations(await currentUser.getIdToken(), false);
                if (!cancelled) setConversations(data);
            } catch (loadError) {
                console.error("Unable to load conversations for Chat search", loadError);
            }
        };
        void loadConversations();
        return () => { cancelled = true; };
    }, [currentUser]);

    useEffect(() => {
        setResults([]);
        setPage(1);
        setHasMore(false);
        setError("");
        if (!currentUser || !canSearch) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            try {
                const data = await searchMyChatMessages(await currentUser.getIdToken(), {
                    query: query.trim() || undefined,
                    conversationId: conversationId || undefined,
                    filesOnly,
                    includeThreads,
                    pageIndex: 1,
                    pageSize,
                });
                if (!cancelled) {
                    setResults(data);
                    setHasMore(data.length === pageSize);
                }
            } catch (searchError: any) {
                if (!cancelled) setError(searchError?.message || "Search failed.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [canSearch, conversationId, currentUser, filesOnly, includeThreads, query]);

    const conversationLookup = useMemo(() => new Map(conversations.map(conversation => [conversation.id, conversation])), [conversations]);
    const conversationOptions = useMemo<ConversationOption[]>(() => [
        { value: 0, label: "All conversations" },
        ...conversations.map(conversation => ({
            value: conversation.id,
            label: conversationName(conversation, currentUser?.uid),
        })),
    ], [conversations, currentUser?.uid]);
    const selectedConversation = conversationOptions.find(option => option.value === conversationId) ?? conversationOptions[0];

    const loadMore = async () => {
        if (!currentUser || loadingMore) return;
        const nextPage = page + 1;
        setLoadingMore(true);
        try {
            const data = await searchMyChatMessages(await currentUser.getIdToken(), {
                query: query.trim() || undefined,
                conversationId: conversationId || undefined,
                filesOnly,
                includeThreads,
                pageIndex: nextPage,
                pageSize,
            });
            setResults(previous => [...previous, ...data.filter(item => !previous.some(existing => existing.id === item.id))]);
            setPage(nextPage);
            setHasMore(data.length === pageSize);
        } finally {
            setLoadingMore(false);
        }
    };

    const openResult = (message: Message) => {
        const queryString = message.threadRootMessageId
            ? `?thread=${message.threadRootMessageId}&message=${message.id}`
            : `?message=${message.id}`;
        navigate(`/chat/${message.conversationId}${queryString}`);
    };

    const toggleBookmark = async (messageId: number) => {
        if (!currentUser) return;
        const previousValue = Boolean(results.find(message => message.id === messageId)?.isBookmarked);
        setResults(previous => previous.map(message => message.id === messageId ? { ...message, isBookmarked: !previousValue } : message));
        try {
            const isBookmarked = await toggleChatBookmark(await currentUser.getIdToken(), messageId);
            setResults(previous => previous.map(message => message.id === messageId ? { ...message, isBookmarked } : message));
        } catch (bookmarkError) {
            setResults(previous => previous.map(message => message.id === messageId ? { ...message, isBookmarked: previousValue } : message));
            console.error("Unable to update saved message from Chat search", bookmarkError);
        }
    };

    return (
        <section className="dark-page-canvas flex min-h-0 flex-1 flex-col bg-slate-50/70 dark:bg-slate-950">
            <header className="dark-surface shrink-0 bg-white px-5 py-4 dark:bg-slate-900 sm:px-7">
                <div className="mx-auto flex max-w-5xl items-start gap-3">
                    <span className="dark-accent-tile mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-[#7E57C2] dark:bg-purple-400/15 dark:text-purple-300"><Search size={18} /></span>
                    <div>
                        <h1 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Search</h1>
                        <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">Find messages and files across conversations you belong to</p>
                    </div>
                </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-7">
                <div className="mx-auto max-w-5xl">
                    <div className="dark-surface rounded-2xl bg-white p-3 shadow-[0_8px_24px_rgba(15,23,42,0.06)] dark:bg-slate-900 dark:shadow-[0_14px_36px_rgba(0,0,0,0.22)]">
                        <label className="chat-search-field flex items-center gap-2 rounded-xl bg-slate-100 px-3 dark:bg-slate-800">
                            <Search size={17} className="shrink-0 text-slate-400" />
                            <input
                                autoFocus
                                type="search"
                                value={query}
                                onChange={event => setQuery(event.target.value)}
                                placeholder="Search messages or filenames"
                                className="chat-search-input h-11 min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-800 outline-none ring-0 shadow-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
                            />
                            {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="rounded-md p-1 text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"><X size={14} /></button>}
                        </label>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><SlidersHorizontal size={12} /> Filters</span>
                            <Select<ConversationOption, false>
                                value={selectedConversation}
                                options={conversationOptions}
                                onChange={(option: SingleValue<ConversationOption>) => setConversationId(option?.value ?? 0)}
                                aria-label="Filter by conversation"
                                className="form-v2-react-select react-select-container react_slect_sm chat-conversation-select min-w-[220px] max-w-[360px] flex-1 text-xs"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                isSearchable
                            />
                            <button type="button" onClick={() => setFilesOnly(current => !current)} aria-pressed={filesOnly} className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium transition ${filesOnly ? "bg-purple-100 text-[#6847A3] dark:bg-purple-400/15 dark:text-purple-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"}`}><FileText size={13} /> Files only</button>
                            <button type="button" onClick={() => setIncludeThreads(current => !current)} aria-pressed={includeThreads} className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium transition ${includeThreads ? "bg-purple-100 text-[#6847A3] dark:bg-purple-400/15 dark:text-purple-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"}`}><CornerDownRight size={13} /> Threads</button>
                        </div>
                    </div>

                    <div className="mt-3 space-y-1.5">
                        {loading ? Array.from({ length: 6 }).map((_, index) => (
                            <div key={index} className="dark-surface flex animate-pulse gap-3 rounded-xl bg-white px-3 py-3 dark:bg-slate-900"><div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-800" /><div className="flex-1"><div className="h-3 w-1/3 rounded bg-slate-200 dark:bg-slate-800" /><div className="mt-2 h-3 w-3/4 rounded bg-slate-100 dark:bg-slate-800/60" /></div></div>
                        )) : error ? (
                            <div className="rounded-xl bg-red-50 p-4 text-center text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">{error}</div>
                        ) : results.length === 0 ? (
                            <div className="py-16 text-center text-sm text-slate-500 dark:text-slate-400">{canSearch ? "No matching messages found." : "Enter at least two characters, or search all shared files."}</div>
                        ) : results.map(message => (
                            <article key={message.id} className="dark-surface group flex items-start gap-3 rounded-xl bg-white px-3 py-3 shadow-[0_2px_10px_rgba(15,23,42,0.04)] transition hover:bg-purple-50/40 dark:bg-slate-900 dark:shadow-none dark:hover:bg-slate-800">
                                <MilestoneAvatar size="xxs" userId={message.author?.id || ""} displayName={authorName(message)} alternate />
                                <button type="button" onClick={() => openResult(message)} className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left">
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                        <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">{authorName(message)}</span>
                                        <span className="truncate text-[10px] font-medium text-[#7E57C2] dark:text-purple-300">{conversationName(conversationLookup.get(message.conversationId), currentUser?.uid)}</span>
                                        <time className="ml-auto shrink-0 text-[10px] text-slate-400 dark:text-slate-500">{new Date(message.createDate).toLocaleString()}</time>
                                    </div>
                                    <p className="mb-0 mt-1 line-clamp-2 text-xs leading-5 text-slate-600 dark:text-slate-300">{messageText(message)}</p>
                                    {(message.attachmentUrl || message.threadRootMessageId) && <div className="mt-1 flex gap-2 text-[10px] text-slate-400 dark:text-slate-500">{message.attachmentUrl && <span className="flex items-center gap-1"><FileText size={10} /> File</span>}{message.threadRootMessageId && <span className="flex items-center gap-1"><CornerDownRight size={10} /> Thread</span>}</div>}
                                </button>
                                <button type="button" onClick={() => void toggleBookmark(message.id)} aria-label={message.isBookmarked ? "Remove saved message" : "Save message"} className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${message.isBookmarked ? "bg-purple-100 text-[#6847A3] dark:bg-purple-400/15 dark:text-purple-200" : "text-slate-400 hover:bg-slate-100 hover:text-[#7E57C2] dark:hover:bg-slate-700"}`}><Bookmark size={14} fill={message.isBookmarked ? "currentColor" : "none"} /></button>
                            </article>
                        ))}
                        {hasMore && <button type="button" onClick={() => void loadMore()} disabled={loadingMore} className="flex h-10 w-full items-center justify-center gap-2 rounded-xl text-xs font-semibold text-[#6847A3] hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-400/10">{loadingMore && <Loader2 size={14} className="animate-spin" />} Load more</button>}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ChatGlobalSearchView;
